import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { useSnapshot } from "valtio";
import { CheckCircle2 } from "lucide-react";
import { appStore } from "@store/appStore";
import { myTodosStore } from "@store/myTodosStore";
import { MyTodosService } from "@services/myTodos/MyTodosService";
import { EngagementAction } from "@models/engagementAction";
import { Todo } from "@models/todo.model";
import Loader from "src/components/common/loader/loader";
import ActionRow from "./components/ActionRow";
import TodoRow from "./components/TodoRow";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import "./styles.scss";

export default function MyTodosPage() {
  useDocumentTitle("My Todos");
  const { loggedInUser } = useSnapshot(appStore);
  const userId = loggedInUser?.id;

  const [actions, setActions] = useState<EngagementAction[]>([]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [todosLoading, setTodosLoading] = useState(true);
  const [actionsError, setActionsError] = useState<string | null>(null);
  const [todosError, setTodosError] = useState<string | null>(null);

  const fetchActions = async () => {
    if (!userId) return;
    setActionsLoading(true);
    setActionsError(null);
    try {
      const service = new MyTodosService();
      const result = await service.listActions(userId);
      setActions(result);
      myTodosStore.actionsCount = result.length;
    } catch {
      setActionsError("Failed to load actions.");
    } finally {
      setActionsLoading(false);
    }
  };

  const fetchTodos = async () => {
    if (!userId) return;
    setTodosLoading(true);
    setTodosError(null);
    try {
      const service = new MyTodosService();
      const result = await service.listTodos(userId);
      setTodos(result);
    } catch {
      setTodosError("Failed to load todos.");
    } finally {
      setTodosLoading(false);
    }
  };

  useEffect(() => {
    fetchActions();
    fetchTodos();
     
  }, [userId]);

  const loading = actionsLoading || todosLoading;
  const error = actionsError ?? todosError;
  const retry = () => { fetchActions(); fetchTodos(); };

  const actionTodoIds = new Set(actions.map((a) => a.todo_id));
  const standaloneTodos = todos.filter((t) => !actionTodoIds.has(t.id));
  const total = actions.length + standaloneTodos.length;

  return (
    <div className="p-3 h-100">
      <Card className="custom-card h-100 d-flex flex-column">
        <Card.Header className="card-header flex-shrink-0 d-flex align-items-center justify-content-between">
          <Card.Title className="mb-0">Todos ({total})</Card.Title>
        </Card.Header>
        <Card.Body className="flex-grow-1 mt-page" style={{ minHeight: 0, overflow: "auto" }}>
          {loading && <Loader />}
          {!loading && error && (
            <div className="mt-page__error">
              {error}
              <button onClick={retry}>Retry</button>
            </div>
          )}
          {!loading && !error && total === 0 && (
            <div className="mt-page__empty-hero">
              <div className="mt-page__empty-hero-icon">
                <CheckCircle2 size={48} strokeWidth={1.5} />
              </div>
              <div className="mt-page__empty-hero-title">You're all caught up</div>
              <div className="mt-page__empty-hero-subtitle">
                Nothing waiting on you right now. New approvals and todos will show up here.
              </div>
            </div>
          )}
          {!loading && !error && actions.map((a) => (
            <ActionRow key={`a-${a.id}`} action={a} />
          ))}
          {!loading && !error && standaloneTodos.map((t) => (
            <TodoRow key={`t-${t.id}`} todo={t} />
          ))}
        </Card.Body>
      </Card>
    </div>
  );
}
